import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "api/baseurl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  TextField,
  MenuItem,
  Card,
  Modal,
  Box,
  Grid,
  Autocomplete,
  IconButton,
  CircularProgress,
  Button,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { encryptBody } from "encryptionAndDecryption/bodyEncryption";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";
import AddIcon from "@mui/icons-material/Add";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { formatDateDDMMYYYY } from "utils/dateFormatter";



const DEFAULT_MAPPING = (currentDate) => ({
  prescription: "",
  drugSearch: "",
  duration: [],
  date: currentDate,
  description: "",
  imageMeta: "",
  image: "",
});

function AddPrescription({ isOpen, onRequestClose, id,onAdded  }) {
  const navigate = useNavigate();

  // current date in YYYY-MM-DD
  const date = new Date();
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const currentDate = `${year}-${month}-${day}`;

  // states
  const [prescriptionQuery, setPrescriptionQuery] = useState("");
const [prescriptionOptions, setPrescriptionOptions] = useState([]);
const [loadingPrescription, setLoadingPrescription] = useState(false);
  const [loading, setLoading] = useState(false); // global overlay loading
  const [doctorTypes, setDoctorTypes] = useState([]);
  const [doctorNames, setDoctorNames] = useState([]);
  const [doctorInput, setDoctorInput] = useState("");
  const [mappingDetails, setMappingDetails] = useState({
    doctorType: "",
    doctorid: "",
  });
  const [drugOptions, setDrugOptions] = useState([]);
  


  const [customerDetails, setCustomerDetails] = useState({
    patientid: id,
    doctor: null,
    description:"",
    date:"",
    image:"",
    imageMeta:"",
    mapping: [DEFAULT_MAPPING(currentDate)],
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  // refs for debouncing
  const doctorSearchTimerRef = useRef(null);
  const drugSearchTimerRef = useRef(null);

  // simple styles reused
  const inputStyle = {
    "& .MuiInputBase-root": {
      height: "45px",
    },
  };

  // Fetch doctor types once
  useEffect(() => {
    fetchDoctorTypes();
    // ensure mapping patient id updates if prop id changes
  }, []);

  useEffect(() => {
    setCustomerDetails((prev) => ({ ...prev, patientid: id }));
  }, [id]);

  // Debounced fetch for doctor names when doctorInput or mappingDetails.doctorType changes
  useEffect(() => {
    if (!mappingDetails.doctorType) {
      setDoctorNames([]);
      return;
    }
    if (doctorSearchTimerRef.current) clearTimeout(doctorSearchTimerRef.current);
    doctorSearchTimerRef.current = setTimeout(() => {
      fetchDoctorNames(mappingDetails.doctorType, doctorInput);
    }, 500); // 500ms debounce
    return () => {
      if (doctorSearchTimerRef.current) clearTimeout(doctorSearchTimerRef.current);
    };
  }, [doctorInput, mappingDetails.doctorType]);

  // Debounced fetch for drug suggestions using pres.drugSearch changes handled inline when a mapping entry changes
  // (fetchDrugs called from onInputChange with debounce implemented per call below)

  // ---------- API calls ----------
  const fetchDoctorTypes = async () => {
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;
      const response = await axios.get("/api/doctortype", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        const decrypted = decryptBody(response.data, secretKey);
        setDoctorTypes(Array.isArray(decrypted) ? decrypted : []);
      }
    } catch (err) {
      console.error("Error fetching doctor types:", err);
    }
  };

  const fetchDoctorNames = async (doctorType, searchTerm = "") => {
    if (!doctorType) return;
    setLoading(true);
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      let response;
      if (searchTerm && searchTerm.trim()) {
        response = await axios.post(
          `/api/doctorregistration/searchByDoctorType/${doctorType}`,
          { searchTerm },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.get(
          `/api/doctorregistration/doctorType/${doctorType}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response.status === 200) {
        const decrypted = decryptBody(response.data, secretKey);
        setDoctorNames(Array.isArray(decrypted) ? decrypted : []);
      } else {
        setDoctorNames([]);
      }
    } catch (err) {
      console.error("Error fetching doctors:", err);
      setDoctorNames([]);
    } finally {
      setLoading(false);
    }
  };

  // drugs from rxnav
//   const fetchDrugs = async (query, index) => {
//     // index is optional; we'll set drugOptions globally for autocomplete
//     if (!query || query.length < 2) {
//       setDrugOptions([]);
//       return;
//     }

//     // debounce per-call
//     if (drugSearchTimerRef.current) clearTimeout(drugSearchTimerRef.current);
//     drugSearchTimerRef.current = setTimeout(async () => {
//       try {
//         const response = await fetch(
//           `https://rxnav.nlm.nih.gov/REST/drugs.json?name=${encodeURIComponent(query)}`
//         );
//         const data = await response.json();

//         const concepts = data?.drugGroup?.conceptGroup || [];
//         let results = [];

//         concepts.forEach((group) => {
//           if (Array.isArray(group?.conceptProperties)) {
//             group.conceptProperties.forEach((item) => {
//  if (item?.name && item?.rxcui) {
//               results.push({ name: item.name, rxcui: item.rxcui });
//             }            });
//           }
//         });

//         // unique & limited
//       const unique = Array.from(new Map(results.map((item) => [item.name, item])).values()).slice(0, 50);
//         setDrugOptions(unique);

//         // optionally set the single mapping's suggestion list (we maintain global list)
//       } catch (error) {
//         console.error("Error fetching drugs:", error);
//       }
//     }, 400);
//   };

const fetchRxNormDrugs = (query) => {
  if (!query || query.length < 2) {
    setDrugOptions([]);
    return;
  }

  if (drugSearchTimerRef.current)
    clearTimeout(drugSearchTimerRef.current);

  drugSearchTimerRef.current = setTimeout(async () => {
    try {
      const res = await fetch(
        `https://rxnav.nlm.nih.gov/REST/drugs.json?name=${encodeURIComponent(query)}`
      );
      const data = await res.json();

      const groups = data?.drugGroup?.conceptGroup || [];
      let results = [];

      groups.forEach((group) => {
        group?.conceptProperties?.forEach((item) => {
          if (item?.name && item?.rxcui) {
            results.push({
              label: item.name,
              rxcui: item.rxcui,
            });
          }
        });
      });

      // remove duplicates
      const unique = [
        ...new Map(results.map((i) => [i.label, i])).values(),
      ].slice(0, 50);

      setDrugOptions(unique);
    } catch (error) {
      console.error("RxNorm error:", error);
      setDrugOptions([]);
    }
  }, 400);
};


const handleMainImageChange = (file) => {
  if (!file) return;

  const reader = new FileReader();
  reader.onloadend = () => {
    const base64 = reader.result.split(",")[1]; // remove data:image/*
    setCustomerDetails((prev) => ({
      ...prev,
      image: base64,
      imageMeta: file.name,
    }));
  };
  reader.readAsDataURL(file);
};


  // ---------- utility helpers ----------
  const updateValue = (field, value, index) => {
    const updated = [...customerDetails.mapping];
    updated[index] = { ...updated[index], [field]: value };
    setCustomerDetails({ ...customerDetails, mapping: updated });
    // clear any pres field error when user types
    setErrors((prev) => {
      const clone = { ...prev };
      delete clone[`prescription_${index}`];
      delete clone[`duration_${index}`];
      //delete clone[`date_${index}`];
      return clone;
    });
  };

  const addPrescription = () => {
    setCustomerDetails((prev) => ({
      ...prev,
      mapping: [...prev.mapping, DEFAULT_MAPPING(currentDate)],
    }));
    // clear mapping-level errors
    setErrors((prev) => {
      const clone = { ...prev };
      // remove prescription_* errors (we won't know new indexes here; keep simple)
      return clone;
    });
  };
  

  const removePrescription = (index) => {
    const updated = [...customerDetails.mapping];
    updated.splice(index, 1);
    setCustomerDetails({ ...customerDetails, mapping: updated });

    // remove errors related to that index and shift others down
    setErrors((prev) => {
      const clone = {};
      Object.keys(prev).forEach((k) => {
        const match = k.match(/^(.+)_(\d+)$/);
        if (match) {
          const base = match[1];
          const idx = Number(match[2]);
          if (idx < index) clone[`${base}_${idx}`] = prev[k];
          if (idx > index) clone[`${base}_${idx - 1}`] = prev[k];
        } else {
          clone[k] = prev[k];
        }
      });
      return clone;
    });
  };

  const handleFileChange = (file, index) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const updated = [...customerDetails.mapping];
      updated[index].imageMeta = file.name;
      // base64 without prefix
      const base = reader.result.split(",")[1] || "";
      updated[index].image = base;
      setCustomerDetails({ ...customerDetails, mapping: updated });
    };
    reader.readAsDataURL(file);
  };

  // ---------- validation ----------
  const validateForm = () => {
    let temp = {};

    if (!mappingDetails.doctorType) {
      temp.doctorType = "Doctor type is required";
    }

    if (!customerDetails.doctor) {
      temp.doctor = "Please select a doctor";
    }

   if (!customerDetails.description || customerDetails.description.trim() === "") {
  temp.description = "Description is required";
} else if (customerDetails.description.trim().length < 3) {
  temp.description = "Description must be at least 3 characters.";
}
     

   // Date validation
if (!customerDetails.date || customerDetails.date.trim() === "") {
  temp.date = "Date is required";
} 
// else {
//   const selectedDate = new Date(customerDetails.date);
//   const today = new Date();

//   // remove time for comparison
//   today.setHours(0, 0, 0, 0);
//   selectedDate.setHours(0, 0, 0, 0);

//   if (isNaN(selectedDate.getTime())) {
//     temp.date = "Invalid date";
//   } else if (selectedDate > today) {
//     temp.date = "Date cannot be in the future";
//   }
// }


  //  customerDetails.mapping.forEach((item, index) => {
  // const presValue = item.prescription || item.drugSearch || "";
  // if (presValue.trim() === "") {
  //   temp[`prescription_${index}`] = "Prescription name required";
  // }
  //     if (!item.duration || item.duration.toString().trim() === "") {
  //       temp[`duration_${index}`] = "Select duration";
  //     }
  //     if (!item.date || item.date.toString().trim() === "") {
  //       temp[`date_${index}`] = "Date required";
  //     }
  //   });

       customerDetails.mapping.forEach((item, index) => {
  /* ---------------- Prescription validation ---------------- */
  const prescriptionValue =
    item.prescription?.trim() || item.drugSearch?.trim() || "";

  if (!prescriptionValue) {
    temp[`prescription_${index}`] = "Prescription is required";
  }

  /* ---------------- Duration validation (MULTIPLE SELECT) ---------------- */
  if (!Array.isArray(item.duration) || item.duration.length === 0) {
    temp[`duration_${index}`] = "Select at least one duration";
  }
});


    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  // ---------- submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix the highlighted errors.");
      return;
    }

    setSubmitting(true);
    try {
      const secretKey = await fetchSecretKey();

      // clean mapping - remove drugSearch helper property before sending
      const cleanedMapping = customerDetails.mapping.map((item) => ({
      ...item,
      prescription: item.prescription || item.drugSearch || "", // pick typed or selected name
    }));

      const rawPayload = {
        patientid: customerDetails.patientid,
        doctorid: customerDetails.doctor?.id || customerDetails.doctor?._id || null,
        description:customerDetails.description || null,
        date:formatDateDDMMYYYY(customerDetails.date) || null,
        image: customerDetails.image || null,
        imageMeta: customerDetails.imageMeta || null,
 mapping: customerDetails.mapping.map((item) => ({
    prescription: item.prescription || item.drugSearch || "",
    // drugSearch: item.drugSearch || "",
    //date: item.date || "",
    //description: item.description || "",
    //duration: item.duration || "",
    duration:item.duration || [],
    //image: item.image || "",
    //imageMeta: item.imageMeta || "",
  })),      };

      const encryptedData = encryptBody(rawPayload, secretKey);
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      const response = await axios.post(
        "/api/prescriptiondetails/create",
        encryptedData,
        {
          headers: {
            "Content-Type": "text/plain",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const decryptedResponse = decryptBody(response.data, secretKey);
        if (decryptedResponse?.success) {
          toast.success(decryptedResponse.message || "Prescription added successfully!");
          // reset form or close
          onRequestClose();
          if (typeof onAdded === "function") onAdded();
          // optionally reset to default
          setCustomerDetails({
            patientid: id,
            doctor: null,
            mapping: [DEFAULT_MAPPING(currentDate)],
          });
          setMappingDetails({ doctorType: "", doctorid: "" });
          setErrors({});
        } else {
          toast.error(decryptedResponse?.message || "Failed to add prescription.");
        }
      } else {
        toast.error("Failed to submit. Please try again.");
      }
    } catch (error) {
      console.error("Submit error:", error);
      const message =
        error?.response?.data?.message || error?.message || "Submission failed";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- UI ----------
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Modal open={isOpen} onClose={onRequestClose}>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          p={2}
        >
          <Card
            sx={{
              width: { xs: "95%", sm: "90%", md: "900px" },
              padding: 3,
              margin: "auto",
              marginTop: "4vh",
              maxHeight: "90vh",
              overflowY: "auto",
              borderRadius: 2,
              boxShadow: 6,
            }}
          >
            {/* loading overlay */}
            {(loading || submitting) && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(255,255,255,0.6)",
                  zIndex: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <MDBox pt={2} pb={1} px={2}>
                <MDTypography variant="h5" mb={2} textAlign="center">
                  Add Prescription
                </MDTypography>

                <Grid container spacing={2}>
                  {/* Doctor Type */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="Doctor Type"
                      placeholder="Select doctor type"
                      fullWidth
                      value={mappingDetails.doctorType || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setMappingDetails((prev) => ({
                          ...prev,
                          doctorType: val,
                        }));
                        // reset selected doctor when type changes
                        setCustomerDetails((prev) => ({ ...prev, doctor: null }));
                        setDoctorInput("");
                        // doctor names will be fetched by debounce effect
                      }}
                      sx={inputStyle}
                      error={Boolean(errors.doctorType)}
                      helperText={errors.doctorType}
                    >
                      <MenuItem value="">Select Doctor Type</MenuItem>
                      {doctorTypes.map((dt) => (
                        <MenuItem key={dt.doctor_type} value={dt.doctor_type}>
                          {dt.doctor_type}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Doctor Name */}
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={Array.isArray(doctorNames) ? doctorNames : []}
                      getOptionLabel={(option) =>
                        option ? `${option.firstname || ""} ${option.lastname || ""}`.trim() : ""
                      }
                      value={customerDetails.doctor || null}
                      onChange={(e, newValue) =>
                        setCustomerDetails((prev) => ({ ...prev, doctor: newValue }))
                      }
                      inputValue={doctorInput}
                      onInputChange={(e, newInputValue) => setDoctorInput(newInputValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Doctor"
                          placeholder=" search or select  doctor"
                          error={Boolean(errors.doctor)}
                          helperText={errors.doctor}
                          sx={inputStyle}
                        />
                      )}
                    />
                  </Grid>

                                        {/* Description */}
                        <Grid item xs={12}>
                          <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={3}
                            value={customerDetails.description || ""}
                            onChange={(e) =>
                              setCustomerDetails((prev) => ({
                                ...prev,
                                description: e.target.value,
                              }))
                            }
                            placeholder="Enter additional instructions"
                            error={Boolean(errors.description)}
                          helperText={errors.description}
                          />
                        </Grid>

                       {/* File upload */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ width: "100%" }}>
                          <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
                            Upload Image (optional)
                          </Typography>

                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleMainImageChange(e.target.files[0])}
                            style={{ width: "100%" }}
                          />

                          {uploadedFile && (
                            <Typography variant="caption" sx={{ mt: 1, color: "green" }}>
                              Selected: {uploadedFile.name}
                            </Typography>
                          )}
                        </Box>
                      </Grid>


                        {/* Date */}
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Date"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={customerDetails.date || ""}
                            onChange={(e) =>
                              setCustomerDetails((prev) => ({
                                ...prev,
                                date: e.target.value,
                              }))
                            }
                            sx={inputStyle}
                            error={Boolean(errors.date)}
                          helperText={errors.date}
                          />
                        </Grid>
                                          
                  
    {/* ✅ PRESCRIPTION LIST */}
    {customerDetails.mapping.map((pres, index) => (
      <Grid container spacing={2} mt={2} key={index}>
        {/* Prescription */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            freeSolo
            options={drugOptions}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            inputValue={pres.drugSearch || ""}
            onInputChange={(e, value) => {
              updateValue("drugSearch", value, index);
              fetchRxNormDrugs(value);
            }}
            onChange={(e, newValue) => {
              if (typeof newValue === "string") {
                updateValue("prescription", newValue, index);
                updateValue("rxcui", null, index);
              } else {
                updateValue("prescription", newValue?.label || "", index);
                updateValue("rxcui", newValue?.rxcui || null, index);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Prescription"
                placeholder="Search medicine (RxNorm)"
                fullWidth
                error={Boolean(errors[`prescription_${index}`])}
                helperText={errors[`prescription_${index}`]}
              />
            )}
          />
        </Grid>

       {/* ✅ Duration dropdown */}
<Grid item xs={12} md={4}>
  <TextField
    select
    label="Duration"
    fullWidth
    SelectProps={{
      multiple: true,
      renderValue: (selected) =>
        selected.length === 0
          ? "Select Duration"
          : selected.join(", "),
    }}
    value={pres.duration || []}
    onChange={(e) => {
      const value = e.target.value;
      updateValue("duration", value, index); // value is array
    }}
    sx={inputStyle}
    error={Boolean(errors[`duration_${index}`])}       
     helperText={errors[`duration_${index}`]} 
  >
    {[
      "Morning - Before Breakfast",
      "Morning - After Breakfast",
      "Afternoon - Before Lunch",
      "Afternoon - After Lunch",
      "Night - Before Dinner",
      "Night - After Dinner",
    ].map((time) => (
      <MenuItem key={time} value={time}>
        <Checkbox checked={pres.duration?.includes(time)} />
        <ListItemText primary={time} />
      </MenuItem>
    ))}
  </TextField>
</Grid>


                  {/* Add / Remove */}
          <Grid item xs={12} md={2} display="flex" alignItems="center">
            {/* ➕ Add only on last row */}
            {index === customerDetails.mapping.length - 1 && (
              <IconButton onClick={addPrescription}>
                <AddIcon />
              </IconButton>
            )}

            {/* 🗑️ Delete only for extra prescriptions (index > 0) */}
            {index > 0 && (
              <IconButton onClick={() => removePrescription(index)}>
                <DeleteIcon />
              </IconButton>
            )}
          </Grid>

      </Grid>
    ))}


                </Grid>

                {/* Prescriptions list */}
                {/* <MDBox mt={3}> */}
                  {/* {customerDetails.mapping.map((pres, index) => (
                    <Box
                      key={index}
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        padding: 2,
                        mb: 2,
                        position: "relative",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      {customerDetails.mapping.length > 1 && (
                        <IconButton
                          onClick={() => removePrescription(index)}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                          }}
                          title="Remove"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}

                      {/* <Grid container spacing={2}> */}
                        {/* Prescription/Drug name with suggestions */}
                        {/* <Grid item xs={12} md={8}>
                          <Autocomplete
  freeSolo
  options={drugOptions}
  getOptionLabel={(option) => option.name || ""}
  value={pres.prescription ? { name: pres.prescription, rxcui: pres.rxcui } : null}
  inputValue={pres.drugSearch || ""}
  onInputChange={(e, newInputValue) => {
    updateValue("drugSearch", newInputValue, index);
    fetchDrugs(newInputValue, index);
  }}
  onChange={(e, newValue) => {
    updateValue("prescription", newValue?.name || pres.drugSearch || "", index);
    updateValue("rxcui", newValue?.rxcui || null, index);
    updateValue("drugSearch", newValue?.name || pres.drugSearch || "", index);
  }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Prescription"
      placeholder="Search Prescription..."
      fullWidth
      error={Boolean(errors[`prescription_${index}`])}
      helperText={errors[`prescription_${index}`]}
      sx={inputStyle}
    />
  )}
/>

                        </Grid> */}

                        {/* Duration */}
                        {/* <Grid item xs={12} md={4}>
                          <TextField
                            select
                            label="Duration"
                            fullWidth
                            value={pres.duration || ""}
                            onChange={(e) => updateValue("duration", e.target.value, index)}
                            error={Boolean(errors[`duration_${index}`])}
                            helperText={errors[`duration_${index}`]}
                            sx={inputStyle}
                          >
                            <MenuItem value="">Select</MenuItem>
                            <MenuItem value="Morning - Before Breakfast">
                              Morning - Before Breakfast
                            </MenuItem>
                            <MenuItem value="Morning - After Breakfast">
                              Morning - After Breakfast
                            </MenuItem>
                            <MenuItem value="Afternoon - Before Lunch">
                              Afternoon - Before Lunch
                            </MenuItem>
                            <MenuItem value="Afternoon - After Lunch">
                              Afternoon - After Lunch
                            </MenuItem>
                            <MenuItem value="Night - Before Dinner">
                              Night - Before Dinner
                            </MenuItem>
                            <MenuItem value="Night - After Dinner">
                              Night - After Dinner
                            </MenuItem>
                          </TextField>

                          
                        </Grid> */}

                        {/* <Grid item xs={12} md={4}> */}
                              {/* PLUS ICON BUTTON */}
    {/* {index === customerDetails.mapping.length - 1 && (
      <IconButton
        color="primary"
        onClick={addPrescription}
        sx={{
          border: "1px solid #1976d2",
          borderRadius: "6px",
          height: "45px",
          width: "45px",
        }}
      >
        <AddIcon />
      </IconButton>
    )}
                        </Grid> */}

                       

                       
                      {/* </Grid> */}
                    {/* </Box>
                  // ))} */}
                   {/* */} 

                 
                {/* </MDBox> */}

                {/* Actions */}
                <MDBox mt={3} textAlign="center">
                  <MDButton
                    variant="gradient"
                    color="info"
                    type="submit"
                    sx={{
                      background: "#2e7faeff",
                      color: "#fff",
                      "&:hover": { background: "#2e7faeff" },
                      mr: 1,
                      px: 3,
                    }}
                    disabled={submitting}
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </MDButton>

                  <MDButton
                    variant="gradient"
                     color="info"
                     
                    onClick={() => {
                      // reset and close
                      setCustomerDetails({
                        patientid: id,
                        doctor: null,
                        mapping: [DEFAULT_MAPPING(currentDate)],
                      });
                      setMappingDetails({ doctorType: "", doctorid: "" });
                      setErrors({});
                      onRequestClose();
                    }}
                    sx={{
                      background: "#2e7faeff",
                      color: "#fff",
                      "&:hover": { background: "#2e7faeff" },
                      mr: 1,
                      px: 3,
                    }}
                  >
                    Cancel
                  </MDButton>
                </MDBox>
              </MDBox>
            </form>
          </Card>
        </MDBox>
      </Modal>
    </DashboardLayout>
  );
}

export default AddPrescription;
